/**
 * Parses a CMS-authored price into a number for ranking (CMS-1862).
 *
 * Strapi stores these in free-text fields, so `"1,280"` and `"HK$1,280"` are
 * both realistic — and a bare `parseFloat` would silently read them as `1`,
 * putting the most expensive plan first. Grouping separators, a leading
 * currency prefix and stray spaces are stripped; anything still not a plain
 * number (`"188 - 250"`, `"n/a"`, an empty string) is rejected as unpriced
 * rather than guessed at, so the caller sinks it to the bottom instead of
 * ranking on a wrong value.
 *
 * The tolerance is deliberately asymmetric: a *leading* currency prefix
 * (`"HK$1,280"`, `"HKD 1,280"`) is stripped because that is how editors most
 * often type the field, while a *trailing* unit (`"1,280 HKD"`, `"1280元"`) is
 * rejected — `AttrNumber` carries dedicated `prefix` / `suffix` fields, so
 * `value` is meant to be the bare number, and sinking the card beats inventing
 * a number for input that could equally be a range or a note.
 */
export function parsePriceValue(
  raw: string | undefined | null,
): number | undefined {
  if (typeof raw !== 'string') return undefined;

  const cleaned = raw
    .trim()
    // Leading currency prefix only — "HK$", "S$ ", "HKD", "$". Letters anywhere
    // else ("from 188", "n/a") stay put so the check below rejects them.
    .replace(/^[A-Za-z]{0,3}\s*\p{Sc}?\s*/u, '')
    // Whitespace inside the number — `\s` covers the non-breaking and thin
    // spaces editors paste, so no separate term is needed for them.
    .replace(/\s/g, '')
    // Thousands separators: only between digit groups, so a decimal comma
    // ("1,5") is not silently turned into "15". Written with a capture group
    // rather than a lookbehind — lookbehind is a regex runtime feature that
    // cannot be transpiled, and would throw at parse time on an older engine,
    // taking the whole results-page chunk with it.
    .replace(/(\d),(?=\d{3}(\D|$))/g, '$1');

  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return undefined;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}
